import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CardProducto from './CardProducto.jsx';
import Cookies from 'js-cookie';
import { useEditor } from '../../App.jsx';
import { confirmDelete, showAlert } from '../../components/HelperAlerts/HelpersAlerts.jsx';
import { getThemeContext } from '../../components/providers/ThemeProvider.jsx';
import DropdownTipoVista from './DropdownTipoVista.jsx';
import { PlusIcon } from '@heroicons/react/20/solid';
import { Package, Edit, Trash2 } from 'lucide-react';

const AdminPanelProductos = () => {
    const [products, setProducts] = useState([]);
    const theme = getThemeContext();

    const [viewMode, setViewMode] = useState(() => {
        const saved = Cookies.get('viewModeProductos');
        return saved ? JSON.parse(saved) : { mode: 'grid', columns: 3, sortBy: 'created_at', sortDirection: 'asc' };
    });

    useEffect(() => {
        fetchProducts();
        Cookies.set('viewModeProductos', JSON.stringify(viewMode), { expires: 7 });
    }, [viewMode]);

    const fetchProducts = () => {
        axios
            .get('/products/', {
                params: {
                    ordering: viewMode.sortDirection === 'desc' ? `-${viewMode.sortBy}` : viewMode.sortBy,
                },
            })
            .then(res => setProducts(Array.isArray(res.data) ? res.data : (res.data.results ?? [])))
            .catch(err => console.error('Error fetching products:', err));
    };

    const handleDelete = async (productId) => {
        const confirmed = await confirmDelete({
            title: '¿Eliminar producto?',
            text: 'Esta acción eliminará el producto de forma permanente.',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            theme,
        });
        if (!confirmed) return;
        try {
            await axios.delete(`/products/${productId}/`);
            setProducts(prev => prev.filter(p => p.id !== productId));
            showAlert({ title: 'Eliminado', text: 'El producto ha sido eliminado correctamente.', icon: 'success', theme });
        } catch {
            showAlert({ title: 'Error', text: 'Hubo un problema al eliminar el producto.', icon: 'error', theme });
        }
    };

    const { stateEditor, setStateEditor } = useEditor();

    const handleAddProduct = () => {
        setStateEditor({ ...stateEditor, editor: true, editorID: 'add', editorType: 'producto_editor' });
    };

    const handleEditProduct = (productId) => {
        setStateEditor({ ...stateEditor, editor: true, editorID: productId, editorType: 'producto_editor' });
    };

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">
            {/* Toolbar */}
            <div className="glassCard rounded-xl p-3 z-40 flex flex-wrap gap-2 items-center justify-between">
                <button className="btn btn-sm btn-primary gap-1" onClick={handleAddProduct}>
                    <PlusIcon className="w-4 h-4" /> Añadir producto
                </button>
                <DropdownTipoVista
                    options={[
                        { label: '2 columnas', value: { mode: 'grid', columns: 2 } },
                        { label: '3 columnas', value: { mode: 'grid', columns: 3 } },
                        { label: '4 columnas', value: { mode: 'grid', columns: 4 } },
                        { label: 'Lista',      value: { mode: 'list' } },
                    ]}
                    selected={viewMode.mode}
                    selectedValue={viewMode}
                    onChange={val => setViewMode(prev => ({ ...prev, mode: val.mode, columns: val.columns }))}
                />
            </div>

            {/* Content */}
            <div className={`glassCard rounded-xl flex-1 overflow-y-auto scrollbar_mod p-3 ${products.length === 0 ? 'flex items-center justify-center' : ''}`}>
                {products.length > 0 ? (
                    viewMode.mode === 'grid' ? (
                        <div className={`grid grid-cols-${viewMode.columns} gap-4`}>
                            {products.map(product => (
                                <CardProducto
                                    key={product.id}
                                    product={product}
                                    isAdmin={true}
                                    onDelete={handleDelete}
                                    onEdit={handleEditProduct}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-3">
                            {products.map(product => (
                                <div key={product.id} className="card card-side bg-base-100 shadow items-center px-4 py-3 gap-4">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-base-content truncate">{product.name}</h3>
                                        <p className="text-sm text-base-content/60 truncate">{product.description}</p>
                                        <p className="font-bold text-primary mt-0.5">${product.price}</p>
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        <button className="btn btn-sm btn-outline gap-1" onClick={() => handleEditProduct(product.id)}>
                                            <Edit size={14} /> Editar
                                        </button>
                                        <button className="btn btn-sm btn-outline btn-error gap-1" onClick={() => handleDelete(product.id)}>
                                            <Trash2 size={14} /> Eliminar
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    <div className="flex flex-col items-center gap-2 text-center">
                        <Package size={40} className="text-base-content/20" />
                        <p className="text-base-content/50">No hay productos disponibles.</p>
                        <p className="text-sm text-base-content/40">Haz clic en "Añadir producto" para crear uno nuevo.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPanelProductos;
